import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FilesUploaderService} from '../files-uploader.service';
import {QueueItem} from '../queue-item';
import {FormArray, FormBuilder} from '@angular/forms';
import {Subscription} from 'rxjs';
import {debounceTime} from 'rxjs/operators';
import {UploadedFile} from '@interfaces/file.interface';
import {MimeTypeNamespace} from '@namespaces/mime-type.namespace';
import MimeEnums = MimeTypeNamespace.MimeEnums;
import {PreviewModalService} from '@services/preview-modal.service';


interface FileToPreview {
    id: number|string;
    name: string;
    url: string;
    rawFile: UploadedFile;
    error?: string;
    /**
     * Файл может быть предпросмотрен в браузере
     */
    canPreview?: boolean;
}

@Component({
    selector: 'app-files-uploader',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './files-uploader.component.html'
})
export class FilesUploaderComponent implements OnInit, OnDestroy {

    @Input() multiple = true;
    @Input() maxSize = 10;
    @Input() loadTarget: 'Lab'|'Noap'|'Noap_public' = 'Lab';
    @Input() allowTiff = true;

    /**
     * Разрешена ли расширенная загрузка файлов, включающая нестандартные типы
     */
    @Input() allowExtendedTypes = false;

    @Input() fbControl: FormArray;

    public queue: QueueItem[] = [];

    public filesToPreview: FileToPreview[] = [];

    /**
     * Список доступных по дефолту типов файлов для загрузки
     */
    public mimeTypes: string[] = [
        MimeEnums.imageJPEG,
        MimeEnums.imageJPG,
        MimeEnums.imagePNG,
        MimeEnums.pdf,
    ];

    private subs: Subscription[] = [];

    private updateView$ = new EventEmitter();

    constructor(
        private uploadService: FilesUploaderService,
        private cd: ChangeDetectorRef,
        private fb: FormBuilder,
        private filesPreviewService: PreviewModalService,
    ) {
    }

    ngOnInit() {
        if (this.allowTiff) {
            this.mimeTypes.push(MimeEnums.tiff);
        }

        if (this.allowExtendedTypes) {
            /**
             * Расширенные типы включают в себя все офисные файлы и CSV
             */
            this.mimeTypes.push(...MimeTypeNamespace.getOfficeTypes(), MimeEnums.csv);
        }

        this.updateViewList(this.fbControl.value);

        this.subs.push(
            this.fbControl.valueChanges.pipe(
                debounceTime(30)
            ).subscribe((value) => {
                this.updateViewList(value);
                this.updateView$.emit();
            }),
            this.uploadService.updateProgress$.pipe(
            ).subscribe(() => this.updateView$.emit()),
            this.updateView$.pipe(
                debounceTime(30)
            ).subscribe(() => this.cd.detectChanges())
        );
    }

    showFilePreview(file: FileToPreview) {
        if (!file || !file.canPreview) {
            return;
        }

        this.filesPreviewService.show({
            files: [file.rawFile]
        });
    }

    handleFilesSelect(fileList: FileList) {
        const files: File[] = Object.keys(fileList).map((key) => fileList[key] as File);

        const queuedFilesNames = this.queue.map(q => q.name);

        const newFiles: QueueItem[] = files.filter(f => !queuedFilesNames.includes(f.name)).map(file => new QueueItem(file, file.name));

        newFiles.forEach(item => {
            let message = null;

            const size = item.file.size;
            if ((size / 1024 / 1024) > this.maxSize) {
                message = `Размер файла «${this.getFileNamePreview(item.name)}» превышает допустимый`;
            }

            if (!this.mimeTypes.includes(item.file.type)) {
                message = `Файл «${this.getFileNamePreview(item.name)}» имеет недопустимый формат`;
            }

            if (message) {
                item.setError(message);
            }
        });

        if (this.multiple) {
            this.queue.push(...newFiles);
        } else {
            if (newFiles.length) {
                this.queue = [newFiles[0]];
            }
        }

        this.updateView$.emit();

        this.upload(this.queue);
    }

    upload(queue: QueueItem[] = []) {
        queue.filter(q => !q.progress && !q.error).forEach(item => {
            this.uploadService.upload(item, this.loadTarget).toPromise().then((response: UploadedFile) => {
                this.setControlValue(response);
                this.removeFromQueue(item);
            }, (errorResponse) => {
                item.setError(`Не удалось загрузить файл «${this.getFileNamePreview(item.name)}»`);
                /**
                 * Тут вроде ничего делать не нужно, ибо это уже перехвачено и обработано в сервисе
                 */
            });
        });
    }

    private updateViewList(value: UploadedFile|UploadedFile[]) {
        if (value instanceof Array) {
            this.filesToPreview = value.map((v): FileToPreview => {
                return {
                    id: v.file_id,
                    name: v.file_name,
                    url: v.file_url,
                    rawFile: v,
                    canPreview: MimeTypeNamespace.isFilePreviewableInBrowser(v.file_mime_type),
                };
            });
            return;
        }

        if (value instanceof Object) {
            this.filesToPreview = [{
                id: value.file_id,
                name: value.file_name,
                url: value.file_url,
                rawFile: value,
                canPreview: MimeTypeNamespace.isFilePreviewableInBrowser(value.file_mime_type),
            }];
            return;
        }

        this.filesToPreview = [];
    }

    setControlValue(item: UploadedFile) {
        if (!this.multiple) {
            while (this.fbControl.length) {
                this.fbControl.removeAt(0);
            }
        }

        this.fbControl.push(this.fb.group(item));
        this.fbControl.markAsTouched();
        this.fbControl.markAsDirty();
        this.fbControl.updateValueAndValidity();
    }

    removeFormValue(index: number) {
        this.fbControl.removeAt(index);
    }

    removeFromQueue(item: QueueItem) {
        if (!item) {
            return;
        }

        const index = this.queue.indexOf(item);

        if (index >= 0) {
            this.queue.splice(index, 1);
            this.updateView$.emit();
        }
    }

    ngOnDestroy() {
        this.subs.forEach(s => s.unsubscribe());
        this.subs.length = 0;
    }

    /**
     * Получить представление имени файла, которое будет отображено в итерфейсе
     * @param {string} itemName
     * @returns {string}
     */
    private getFileNamePreview(itemName: string) {
        return itemName.length <= 12 ? itemName : itemName.substr(0, 9) + '...';
    }
}
